import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Popconfirm, Space, Table, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { InboxOutlined, ReloadOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { YouTubeLiveStream, ScheduleChunk } from './types';
import {
  importScheduleChunk,
  listScheduleChunks,
  deleteScheduleChunk,
  getScheduleChunkDownloadUrl,
  ScheduleImportError,
} from './api';

const { Text, Link } = Typography;
const { Dragger } = Upload;

interface ScheduleChunksTabProps {
  stream: YouTubeLiveStream;
  /** Called after a successful import/delete so the parent can refresh stream data. */
  onChanged?: () => void;
}

const STATUS_COLORS: Record<ScheduleChunk['status'], string> = {
  queued: 'blue',
  airing: 'green',
  aired: 'default',
};

/**
 * Format an ISO instant in the stream's broadcast timezone with its zone
 * abbreviation (e.g. "Sep 30, 2026, 08:21:51 EDT"). en-US is used because it
 * renders US zones as EDT/EST rather than "GMT-4". Without a stream timezone
 * the viewer's local zone is used, still labelled.
 */
const formatTime = (iso: string | null, timeZone?: string) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || undefined,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  }).format(date);
};

// Chunk ends are nominal (whole-second row durations), so a handover within
// this tolerance is continuous rather than a gap or overlap.
const HANDOVER_TOLERANCE_MS = 60_000;

const formatSpan = (ms: number) => {
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/** Human-readable file size (e.g. "3.4 MB"); null when the size is unknown. */
const formatBytes = (bytes: number | null): string | null => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
};

/** Gap (positive) or overlap (negative) between a chunk and the one before it. */
const handoverMs = (previous: ScheduleChunk | undefined, chunk: ScheduleChunk): number | null => {
  if (!previous?.ends_at || !chunk.first_schedule_time) return null;
  const diff = Date.parse(chunk.first_schedule_time) - Date.parse(previous.ends_at);
  return Number.isFinite(diff) ? diff : null;
};

/**
 * Schedule tab for Amagi HLS-pull streams: upload a Marathon FAST schedule
 * CSV as a queued monthly chunk, list chunks with their lifecycle status, and
 * delete chunks that are still entirely in the future (the backend enforces
 * both the append-only queue and the immutability rule).
 */
const ScheduleChunksTab: React.FC<ScheduleChunksTabProps> = ({ stream, onChanged }) => {
  const [chunks, setChunks] = useState<ScheduleChunk[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importErrorTitle, setImportErrorTitle] = useState<string>('');

  const loadChunks = useCallback(async () => {
    if (!stream?.id) return;
    setLoading(true);
    try {
      setChunks(await listScheduleChunks(stream.id));
    } catch (error) {
      console.error('Error loading schedule chunks:', error);
      message.error('Failed to load schedule chunks');
    } finally {
      setLoading(false);
    }
  }, [stream?.id]);

  useEffect(() => {
    loadChunks();
  }, [loadChunks]);

  const handleUpload: UploadProps['beforeUpload'] = (file) => {
    (async () => {
      setUploading(true);
      setImportErrors([]);
      setImportErrorTitle('');
      try {
        const result = await importScheduleChunk(stream.id, file);
        message.success(`Imported ${result.row_count} rows as ${result.chunk_id}`);
        await loadChunks();
        onChanged?.();
      } catch (error) {
        if (error instanceof ScheduleImportError) {
          setImportErrorTitle(error.message);
          setImportErrors(error.errors);
        } else {
          message.error('Failed to import schedule CSV');
          console.error('Schedule import failed:', error);
        }
      } finally {
        setUploading(false);
      }
    })();
    return false; // manual upload — never let antd POST it itself
  };

  const handleDelete = async (chunkId: string) => {
    try {
      await deleteScheduleChunk(stream.id, chunkId);
      message.success('Chunk deleted');
      await loadChunks();
      onChanged?.();
    } catch (error: any) {
      const errMsg = error?.response?.data?.error || 'Failed to delete chunk';
      message.error(errMsg);
    }
  };

  // The presigned URL carries a content-disposition header, so opening it in a
  // new tab downloads the backed-up CSV under its original name.
  const handleDownload = async (chunkId: string) => {
    try {
      const { url } = await getScheduleChunkDownloadUrl(stream.id, chunkId);
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Error getting schedule chunk download link:', error);
      message.error('Could not get download link');
    }
  };

  const columns = [
    {
      title: 'Chunk',
      dataIndex: 'chunk_id',
      key: 'chunk_id',
      render: (id: string) => <Text code>{id}</Text>,
    },
    {
      title: 'File',
      dataIndex: 'original_filename',
      key: 'original_filename',
      width: 280,
      ellipsis: true,
      render: (filename: string | null, record: ScheduleChunk) => {
        if (!filename && !record.source_available) return <Text type="secondary">—</Text>;
        const name = filename || 'original schedule CSV';
        const details = [record.uploaded_by, formatBytes(record.byte_size)].filter(Boolean).join(' · ');
        return (
          <>
            {record.source_available ? (
              <Tooltip title={`Download ${name}`}>
                <Link ellipsis onClick={() => handleDownload(record.chunk_id)}>
                  <DownloadOutlined /> {name}
                </Link>
              </Tooltip>
            ) : (
              <Tooltip title="Original file not retained">
                <Text ellipsis>{name}</Text>
              </Tooltip>
            )}
            {details && (
              <div>
                <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                  {details}
                </Text>
              </div>
            )}
          </>
        );
      },
    },
    { title: 'Rows', dataIndex: 'row_count', key: 'row_count', width: 80 },
    {
      title: 'Starts',
      dataIndex: 'first_schedule_time',
      key: 'first_schedule_time',
      render: (iso: string | null, record: ScheduleChunk, index: number) => {
        const diff = handoverMs(chunks[index - 1], record);
        return (
          <Space direction="vertical" size={0}>
            <span>{formatTime(iso, stream.timezone)}</span>
            {diff !== null && diff > HANDOVER_TOLERANCE_MS && (
              <Tag color="orange">{formatSpan(diff)} gap after previous chunk</Tag>
            )}
            {diff !== null && diff < -HANDOVER_TOLERANCE_MS && (
              <Tag color="orange">overlaps previous chunk by {formatSpan(-diff)}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Last program starts',
      dataIndex: 'last_schedule_time',
      key: 'last_schedule_time',
      render: (iso: string | null) => formatTime(iso, stream.timezone),
    },
    {
      title: 'Ends',
      dataIndex: 'ends_at',
      key: 'ends_at',
      render: (iso: string | null) => formatTime(iso, stream.timezone),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: ScheduleChunk['status']) => <Tag color={STATUS_COLORS[status]}>{status}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, record: ScheduleChunk) =>
        record.status === 'queued' ? (
          <Popconfirm
            title="Delete this queued chunk?"
            description="Only chunks entirely in the future can be deleted."
            onConfirm={() => handleDelete(record.chunk_id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Text type="secondary">
        Import a Marathon FAST schedule CSV (from the Scheduler&apos;s export). Each import
        queues as a chunk after the existing schedule; chunks that are airing or have
        aired are immutable.
        {stream.timezone ? ` Broadcast timezone: ${stream.timezone}; times below are shown in it.` : ''}
      </Text>

      <Dragger
        accept=".csv"
        multiple={false}
        showUploadList={false}
        beforeUpload={handleUpload}
        disabled={uploading}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {uploading ? 'Importing…' : 'Click or drag a schedule CSV here to import'}
        </p>
      </Dragger>

      {importErrors.length > 0 && (
        <Alert
          type="error"
          message={importErrorTitle || 'Schedule CSV validation failed'}
          description={
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 240, overflowY: 'auto' }}>
              {importErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          }
          closable
          onClose={() => setImportErrors([])}
        />
      )}
      {importErrorTitle && importErrors.length === 0 && (
        <Alert type="error" message={importErrorTitle} closable onClose={() => setImportErrorTitle('')} />
      )}

      <Table
        size="small"
        rowKey="chunk_id"
        columns={columns}
        dataSource={chunks}
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'No schedule imported yet' }}
        title={() => (
          <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong>Schedule chunks</Text>
            <Button size="small" icon={<ReloadOutlined />} onClick={loadChunks}>
              Refresh
            </Button>
          </Space>
        )}
      />
    </Space>
  );
};

export default ScheduleChunksTab;
